<!---
    /**
    * File and all contents are proprietary work of Executives On The Web Ltd
    * and must not be copied or redistributed without express permission.
    *
    * Creates a signature box
    *
    * @since 20240102
    * @author Thomas Higham <thomash@executivesontheweb.com>
    *
    * @copyright (c) 2024 Executives On The Web Ltd. All right Reserved.
    */
--->
<template>
    <div :id="name + 'outsideDiv'">
        <div v-show="(drawing && !readOnly) || loading" class="signature-wrapper" :style="style">
            <canvas v-b-visible="shown" :id="name" class="signature-pad" :style="style_canvas" :width="width_actual_canvas" :height="height_actual_canvas"></canvas>
        </div>
        <button v-show="drawing && !readOnly" type="button" class="btn btn-sm btn-success mr-2 mt-1" @click="useSignature(false)">Use Signature</button>
        <button v-show="drawing && !readOnly" type="button" class="btn btn-sm btn-danger mr-2 mt-1" @click="clear">Clear</button>
        <button v-show="drawing && !readOnly" type="button" class="btn btn-sm btn-edit mr-2 mt-1" @click="undo">Undo</button>
        <button v-show="drawing && !readOnly && oldValue !== null" type="button" class="btn btn-sm btn-edit mt-1" @click="useSignature(true)">Revert to last signature</button>
        <img v-if="valueActual !== null && valueActual.image !== undefined && valueActual.image !== null && (!drawing || readOnly)" :src="valueActual.image" style="max-width:100%">
        <i v-if="valueActual !== null && valueActual.createdDate !== undefined && valueActual.createdDate !== null && (!drawing || readOnly)"><br />Signed On {{formatDate(valueActual.createdDate)}} at {{displayTime(valueActual.createdDate)}}</i>
        <i v-if="valueActual === null && (!drawing || readOnly)">No Signature Provided</i>
        <button v-show="!drawing && !readOnly" type="button" class="btn btn-sm btn-edit ml-2" @click="redrawSignature">Redraw Signature</button>
    </div>
</template>

<script>
    //https://vuejsexamples.com/vue-signature-pad-component/
    import SignaturePad from 'signature_pad';
    export default{
        props: {
            //the name of the editor
            name: {
                type: String,
                default: 'signature-pad'
            },
            //the value of the editor
            value: {
                type: Object|String,
            },
            //the width in pixels
            width: {
                default: -1
            },
            //the height in pixels
            height: {
                default: -1
            },
            readOnly: {
                type: Boolean,
                default: false
            }
        },
        mounted: function(){
            if(this.valueActual !== undefined && this.valueActual !== null && this.valueActual != '' && this.valueActual.image !== undefined && this.valueActual.image !== null && this.valueActual.image != ''){
                this.drawing = false;
            } else {
                this.drawing = !this.readOnly;
                this.valueActual = null;
            }
            setTimeout(() => this.loadCanvas(), 0.5);
        },
        data() {
            return {
                canvas: null,
                signaturePad: null,
                valueActual: this.value,
                canvasSize: 2000,
                drawing: !this.readOnly,
                loading: true,
                oldValue: null,
                hiddenShownValue: null
            };
        },
        computed: {
            /**
             * gets the style of the outer div to set the width of the element
             * @returns object the style
             */
            style: function(){
                var sty = {};
                if(this.width_actual > 0){
                    sty.width = this.width_actual + 'px';
                }
                if(this.height_actual > 0){
                    sty.height = this.height_actual + 'px';
                }
                return sty;
            },
            /**
             * gets the style of the outer div to set the width of the element
             * @returns object the style
             */
            style_canvas: function(){
                var sty = {};
                if(this.width_actual_canvas > 0){
                    sty.width = this.width_actual_canvas + 'px';
                }
                if(this.height_actual_canvas > 0){
                    sty.height = this.height_actual_canvas + 'px';
                }
                return sty;
            },
            /**
             * gets the height of the object, with a default value
             * @returns int the height in pixels
             */
            height_actual: function(){
                var hei = 200;
                if(this.height > 0){
                    hei = this.height;
                }
                return hei;
            },
            /**
             * gets the height of the object, with a default value
             * @returns int the height in pixels
             */
            width_actual: function(){
                var wid = 500;
                if(this.width > 0){
                    wid = this.width;
                }
                if(this.canvasSize < wid){
                    wid = this.canvasSize;
                }
                return wid;
            },
            /**
             * gets the height of the object, with a default value
             * @returns int the height in pixels
             */
            height_actual_canvas: function(){
                return this.height_actual-4;
            },
            /**
             * gets the height of the object, with a default value
             * @returns int the height in pixels
             */
            width_actual_canvas: function(){
                return this.width_actual-4;
            },
        },
        methods: {
            clear: function(){
                this.signaturePad.clear();
                this.valueActual = null;
            },
            undo: function(){
                var data = this.signaturePad.toData();
                if (data) {
                    data.pop(); // remove the last dot or line
                    this.signaturePad.fromData(data);
                    this.stoppedDrawing();
                }
            },
            useSignature: function(useOld=false){
                this.drawing = false;
                if(useOld){
                    this.valueActual = this.oldValue;
                }
                this.oldValue = null;
                this.$emit('input', this.valueActual);
            },
            redrawSignature: function(){
                this.hiddenShownValue = null;
                this.oldValue = this.valueActual;
                this.valueActual = null;
                this.$emit('input', this.valueActual);
                this.clear();
                this.drawing = true;
            },
            shown: function(isVisible=true){
                if(isVisible){
                    this.loadCanvas();
                } else {
                    if(this.signaturePad !== null){
                        this.hiddenShownValue = this.signaturePad.toData();
                    }
                }
            },
            loadCanvas: function(){
                this.canvas = document.getElementById(this.name);
                this.signaturePad = new SignaturePad(this.canvas);
                this.signaturePad.addEventListener("endStroke", this.stoppedDrawing);
                if(this.hiddenShownValue !== null){
                    if(this.drawing && this.signaturePad !== null){
                        this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value
                        this.signaturePad.fromData(this.hiddenShownValue);
                    }
                    this.hiddenShownValue = null;
                }
                window.addEventListener("resize", this.resizeCanvas);
                setTimeout(() => this.resizeCanvas(), 0.5);
            },
            resizeCanvas: function () {
                if(this.signaturePad !== null){
                    var currentImage = this.signaturePad.toData();
                    //resize canvas on mobile devices
                    var parent = document.getElementById(this.name + 'outsideDiv');
                    var parentWidth = parent.getBoundingClientRect().width;
                    this.canvasSize = parentWidth;
                    var ratio =  Math.max(window.devicePixelRatio || 1, 1);
                    this.canvas.width = this.canvas.offsetWidth * ratio;
                    this.canvas.height = this.canvas.offsetHeight * ratio;
                    this.canvas.getContext("2d").scale(ratio, ratio);
                    this.signaturePad.clear(); // otherwise isEmpty() might return incorrect value
                    this.signaturePad.fromData(currentImage);
                }
                this.loading = false;
            },
            stoppedDrawing: function(self){
                if(this.signaturePad !== undefined && this.signaturePad !== null){
                    this.valueActual = {
                        image: this.signaturePad.toDataURL(),
                        createdDate: new Date(),
                    };
                    this.$emit('input', this.valueActual);
                }
            },
            /**
             * formats a date for displaying
             * 
             * @param Date date the date to display
             * @returns String the formatted date
             */
            formatDate: function (date){
                date = new Date(date);
                var day = date.getDate() + '';
                if(day.length == 1){
                    var day = '0' + day;
                }
                var month = (date.getMonth()+1) + '';
                if(month.length == 1){
                    var month = '0' + month;
                }
                return day + '/' + month + '/' + date.getFullYear();
            },
            /**
             * creates a string of the time of a date for display purposes
             * 
             * @param Date dt the date
             * @return String the formatted time
             */
            displayTime: function(dt){
                if(dt !== null){
                    var date = new Date(dt);
                    //remove any differences caused by daylight savings
                    //date = new Date(date.getTime() + (date.getTimezoneOffset() * 60000))
                    var hours = date.getHours() + '';
                    if(hours.length == 1){
                        var hours = '0' + hours;
                    }
                    var minutes = (date.getMinutes()) + '';
                    if(minutes.length == 1){
                        var minutes = '0' + minutes;
                    }
                    return hours + ':' + minutes;
                }
                else{
                    return '';
                }
            },
        }
    }
</script>